import type { ReactNode } from "react"
import { LogIn } from "lucide-react"
import { ApiError } from "../lib/api"

export interface RequireAuthState {
  isLoading: boolean
  error: ApiError | null
  isAdmin: boolean
}

export interface RequireAuthProps extends RequireAuthState {
  requireAdmin?: boolean
  children: ReactNode
  /**
   * Fallback auth-service base URL used to build a sign-in link when the 401
   * response doesn't carry one. Defaults to https://auth.lilbillbiscuit.com.
   */
  authBase?: string
}

/**
 * Gate wrapper for protected pages. The app supplies the auth-state shape
 * (isLoading/error/isAdmin) from its own /api/me query so this component
 * stays decoupled from any specific API client.
 */
export function RequireAuth({ isLoading, error, isAdmin, requireAdmin, children, authBase }: RequireAuthProps) {
  if (isLoading) {
    return <div className="py-12 text-center text-sm text-th-fg/50">Loading…</div>
  }
  if (error) {
    if (error.status === 401) {
      const loginUrl = error.loginUrl ?? buildFallbackLoginUrl(authBase)
      return <SignInCard loginUrl={loginUrl} />
    }
    if (error.status === 503) {
      return (
        <div className="glass-section text-center">
          <h2 className="font-heading text-2xl">Auth service is unavailable</h2>
          <p className="mt-2 text-sm text-th-fg/60">
            We couldn't reach the sign-in service. Please try again in a minute.
          </p>
        </div>
      )
    }
    return (
      <div className="glass-section text-center">
        <h2 className="font-heading text-2xl">Something went wrong</h2>
        <p className="mt-2 text-sm text-th-fg/60">{error.serverMessage ?? "Unknown error"}</p>
      </div>
    )
  }
  if (requireAdmin && !isAdmin) {
    return (
      <div className="glass-section text-center">
        <h2 className="font-heading text-2xl">Admin access required</h2>
        <p className="mt-2 text-sm text-th-fg/60">Your account doesn't have admin privileges in this app.</p>
      </div>
    )
  }
  return <>{children}</>
}

function buildFallbackLoginUrl(authBase?: string): string {
  const base = (authBase ?? "https://auth.lilbillbiscuit.com").replace(/\/$/, "")
  const next = typeof window !== "undefined" ? window.location.href : "/"
  return `${base}/auth/login?next=${encodeURIComponent(next)}`
}

function SignInCard({ loginUrl }: { loginUrl: string | null }) {
  return (
    <div className="glass-section mx-auto max-w-md text-center">
      <h2 className="font-heading text-2xl">Sign in to continue</h2>
      <p className="mt-2 text-sm text-th-fg/60">
        We use lilbillbiscuit's auth service. Only Sign in with Google is supported.
      </p>
      {loginUrl && (
        <a href={loginUrl} className="btn-primary mt-6">
          <LogIn size={15} /> Sign in with Google
        </a>
      )}
    </div>
  )
}
